import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Paper,
  CircularProgress,
  styled,
  IconButton,
  InputBase,
  Button
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SearchIcon from '@mui/icons-material/Search';
import { getChatDetailByChatId } from 'src/helper/backend_helper';
import { config } from 'src/config';

interface LocationState {
  chatId: any;
  userId: any;
}

const Root = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100vh',
  backgroundColor: '#f5f7fa'
}));

const Sidebar = styled(Paper)(({ theme }) => ({
  width: '20%',
  display: 'flex',
  flexDirection: 'column',
  borderRight: `1px solid #e5e7eb`,
  backgroundColor: '#ffffff',
  overflowY: 'auto',
  boxShadow: 'none'
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid #e5e7eb`
}));

const SearchBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#f3f4f6',
  borderRadius: 8,
  padding: theme.spacing(1, 1.5),
  marginTop: theme.spacing(1.5)
}));

const ChatSection = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#ffffff',
  width: '80%'
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid #e5e7eb`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  backgroundColor: '#ffffff'
}));

const MessagesList = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  padding: theme.spacing(3),
  backgroundColor: '#f9fafb'
}));

const MessageItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'flex',
  gap: theme.spacing(1.5)
}));

const MessageBubble = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: 12,
  wordBreak: 'break-word',
  maxWidth: '65%'
}));

const MessageInfo = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: '#6b7280',
  marginTop: theme.spacing(0.5)
}));

const MessageInputBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid #e5e7eb`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  backgroundColor: '#ffffff'
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  backgroundColor: '#f3f4f6',
  borderRadius: 24,
  padding: theme.spacing(1.5, 2.5),
  fontSize: '0.95rem',
  '& input::placeholder': {
    color: '#9ca3af',
    opacity: 1
  }
}));

const SendButton = styled(Button)(({ theme }) => ({
  borderRadius: 24,
  padding: theme.spacing(1.2, 3),
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  backgroundColor: '#4285f4',
  '&:hover': {
    backgroundColor: '#3367d6',
    boxShadow: '0 4px 12px rgba(66, 133, 244, 0.3)'
  }
}));

function GroupChatMessages() {
  const [chat, setChat] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const location = useLocation();
  const { chatId, userId } = (location.state as LocationState) || {};

  const filteredParticipants =
    chat?.participants?.filter(
      (p: any) =>
        `${p.user.firstName} ${p.user.lastName}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        p.user.email.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  useEffect(() => {
    if (chatId && userId) {
      fetchChatDetails();
    }
  }, [chatId, userId]);

  const fetchChatDetails = async () => {
    try {
      setLoading(true);
      // #404: identity is derived from the JWT on the backend; sending
      // `userId` is now a 400. Body stays empty for the standard request.
      const response = await getChatDetailByChatId(chatId, {});
      setChat(response.data.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      setMessage('');
    }
  };

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );

  if (!chat)
    return (
      <Typography
        variant="h6"
        textAlign="center"
        marginTop={4}
        color="textSecondary"
      >
        Chat not found
      </Typography>
    );

  return (
    <>
      <Helmet>
        <title>{chat.groupName} - Group Chat</title>
      </Helmet>

      <Root>
        {/* Sidebar with team members */}
        <Sidebar elevation={0}>
          <SidebarHeader>
            <Typography variant="h6" fontWeight="700" color="#1f2937">
              {chat.groupName || 'Prestige Labor Solutions Team'}
            </Typography>
            <SearchBox>
              <SearchIcon sx={{ fontSize: 20, color: '#9ca3af' }} />
              <InputBase
                placeholder="Search user"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ ml: 1, flex: 1, fontSize: '0.875rem' }}
              />
            </SearchBox>
          </SidebarHeader>

          <List sx={{ p: 1 }}>
            {filteredParticipants.map((p: any) => (
              <ListItem
                key={p.id}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: '#f3f4f6'
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    alt={`${p.user.firstName} ${p.user.lastName}`}
                    src={config.FILe_URL + p.user.profileImage || ''}
                    sx={{ width: 40, height: 40 }}
                  >
                    {p.user.firstName[0]}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        color="#1f2937"
                      >
                        {p.user.firstName} {p.user.lastName}
                      </Typography>
                      {p.isGroupAdmin && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor: '#dbeafe',
                            color: '#2563eb',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontWeight: 600
                          }}
                        >
                          Admin
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography color="#6b7280" noWrap>
                      {p.user.email || 'Start Conversation'}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Sidebar>

        {/* Chat messages section */}
        <ChatSection>
          {/* Header */}
          <ChatHeader>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: '#6b7280',
                '&:hover': { backgroundColor: '#f3f4f6' }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: '#4285f4'
              }}
            >
              {chat.groupName?.[0] || 'G'}
            </Avatar>
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight="700" color="#1f2937">
                {chat.groupName || 'Group Chat'}
              </Typography>
              <Typography color="#6b7280">
                {chat.shift?.Event?.title || 'Event'} • Shift:{' '}
                {chat.shift?.position || '-'}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                ml: 2,
                pl: 2,
                borderLeft: '1px solid #e5e7eb'
              }}
            >
              <Avatar
                alt={`${chat.participants.find((p: any) => p.user.id === userId)?.user.firstName} ${chat.participants.find((p: any) => p.user.id === userId)?.user.lastName}`}
                src={
                  config.FILe_URL +
                    chat.participants.find((p: any) => p.user.id === userId)
                      ?.user.profileImage || ''
                }
                sx={{ width: 36, height: 36 }}
              >
                {chat.participants.find((p: any) => p.user.id === userId)?.user
                  .firstName[0] || 'U'}
              </Avatar>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color="#1f2937"
                  noWrap
                >
                  {
                    chat.participants.find((p: any) => p.user.id === userId)
                      ?.user.firstName
                  }{' '}
                  {
                    chat.participants.find((p: any) => p.user.id === userId)
                      ?.user.lastName
                  }
                </Typography>
                {/* <Typography variant="caption" color="#6b7280">
                  You
                </Typography> */}
              </Box>
            </Box>
          </ChatHeader>

          {/* Messages */}
          <MessagesList>
            {chat.messages.map((msg: any) => {
              const isCurrentUser = msg.sender.id === userId;
              return (
                <MessageItem
                  key={msg.id}
                  sx={{
                    flexDirection: isCurrentUser ? 'row-reverse' : 'row'
                  }}
                >
                  <Avatar
                    alt={`${msg.sender.firstName} ${msg.sender.lastName}`}
                    src={config.FILe_URL + msg.sender.profileImage || ''}
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: '#1f2937',
                      flexShrink: 0
                    }}
                  >
                    {msg.sender.firstName[0]}
                  </Avatar>
                  <Box flex={1} display="flex" flexDirection="column">
                    {!isCurrentUser && (
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        color="#1f2937"
                        sx={{ mb: 0.5 }}
                      >
                        {msg.sender.firstName} {msg.sender.lastName}
                      </Typography>
                    )}
                    <MessageBubble
                      sx={{
                        backgroundColor: isCurrentUser ? '#4285f4' : '#e5e7eb',
                        color: isCurrentUser ? '#ffffff' : '#1f2937',
                        alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
                        borderTopLeftRadius: isCurrentUser ? 12 : 4,
                        borderTopRightRadius: isCurrentUser ? 4 : 12,
                        padding: msg.type === 'image' ? 0 : undefined
                      }}
                    >
                      {msg.type === 'image' ? (
                        <Box
                          component="img"
                          src={config.FILe_URL + msg.content}
                          alt="chat attachment"
                          sx={{
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: 300,
                            borderRadius: 'inherit'
                          }}
                        />
                      ) : (
                        <Typography variant="body2">{msg.content}</Typography>
                      )}
                    </MessageBubble>
                    <MessageInfo
                      sx={{
                        textAlign: isCurrentUser ? 'right' : 'left',
                        alignSelf: isCurrentUser ? 'flex-end' : 'flex-start'
                      }}
                    >
                      {new Date(msg.createdAt).toLocaleString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </MessageInfo>
                  </Box>
                </MessageItem>
              );
            })}
          </MessagesList>

          {/* Message Input */}
          <MessageInputBox>
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#1f2937' }}>
              {chat.participants.find((p: any) => p.user.id === userId)?.user
                .firstName[0] || 'U'}
            </Avatar>
            <StyledInput
              placeholder="Write your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <IconButton sx={{ color: '#6b7280' }}>
              <AttachFileIcon />
            </IconButton>
            <SendButton
              variant="contained"
              endIcon={<SendIcon />}
              onClick={handleSendMessage}
            >
              Send
            </SendButton>
          </MessageInputBox>
        </ChatSection>
      </Root>
    </>
  );
}

export default GroupChatMessages;
